/**
 * @file NetworkTypeDropdown.js
 * @description A single-select dropdown component for selecting network types.
 */

import React, { useState, useEffect, useMemo } from "react";
import { fetchNetworkTypes } from "../../../services/systemService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * A single-select dropdown component for network types.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {number|string} [props.selectedNetworkType] - The ID of the selected network type.
 * @param {Function} props.onNetworkTypeChange - The callback function to handle changes in selected network type.
 * @param {string} [props.label="Network Type"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.disabled=false] - Determines whether the dropdown is disabled.
 * @param {string} [props.placeholder="Select a network type"] - The placeholder text for the dropdown.
 * @param {string} [props.errorMessage] - The error message to display when validation fails.
 * @param {boolean} [props.hideErrorMessage=false] - Determines whether to hide the error message text.
 * @param {number} [props.tabIndex=0] - The tab order for the dropdown element.
 * @returns {JSX.Element} The NetworkTypeDropdown component.
 */
const NetworkTypeDropdown = ({
  className,
  inlineLabel = true,
  selectedNetworkType,
  onNetworkTypeChange,
  label = "Network Type",
  isRequired = false,
  disabled = false,
  placeholder = "Select a network type",
  errorMessage,
  hideErrorMessage = false,
  tabIndex
}) => {
  const [networkTypes, setNetworkTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadNetworkTypes = async () => {
      setIsLoading(true);
      try {
        const networkTypeData = await fetchNetworkTypes();
        setNetworkTypes(networkTypeData);
      } catch (error) {
        console.error("Could not fetch network types:", error);
        Toast.error(getText('msg_95a4a3799bfc'));
      } finally {
        setIsLoading(false);
      }
    };

    loadNetworkTypes();
  }, []);

  const getDefaultLabel = () => {
    const matchingNetworkType = networkTypes.find((networkType) => networkType.id === selectedNetworkType);
    return matchingNetworkType?.name ?? placeholder;
  };

  const networkTypeOptions = useMemo(
    () => [
      { value: null, label: placeholder },
      ...networkTypes.map((networkType) => ({ value: networkType.id, label: networkType.name })),
    ],
    [networkTypes, placeholder]
  );

  return (
    <fieldset
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className} ${errorMessage ? "is-invalid" : ""}`}
      style={{ position: "relative" }}
    >
      <label htmlFor="network-type-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="network-type-dropdown"
          defaultValue={getDefaultLabel()}
          options={networkTypeOptions}
          value={selectedNetworkType}
          onChange={onNetworkTypeChange}
          disabled={networkTypes.length === 0 || isLoading || disabled}
          tabIndex={tabIndex}
        />
      </LoadingWrapper>
      {errorMessage && !hideErrorMessage && (
        <div className="invalid-feedback d-block">{errorMessage}</div>
      )}
    </fieldset>
  );
};

export default NetworkTypeDropdown;
